﻿import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, CalendarDays, Clock, Heart, KeyRound, MapPin, User, UserCircle2 } from 'lucide-react';
import {
  updateStaffSchema,
  updateStaffProfileSchema,
  STAFF_TYPES,
  STAFF_TYPE_LABELS,
  type ApiError,
  type UpdateStaffInput,
  type UpdateStaffProfileInput,
} from '@pms/shared';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { designationsService } from '@/services/designations.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function SectionHeader({
  icon,
  title,
  optional,
}: {
  icon: React.ReactNode;
  title: string;
  optional?: boolean;
}) {
  return (
    <CardHeader className="border-b border-border/40 px-4 py-2.5">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold text-primary">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
          {icon}
        </div>
        {title}
        {optional && (
          <span className="ml-auto text-xs font-normal text-muted-foreground">Optional</span>
        )}
      </CardTitle>
    </CardHeader>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

export function StaffEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => usersService.get(id),
    enabled: !!id,
  });

  // â”€â”€ Basic info form (users table) â”€â”€
  const { register, trigger, getValues, reset, formState, control } = useForm<UpdateStaffInput>({
    resolver: zodResolver(updateStaffSchema),
  });

  // â”€â”€ Profile form (user_profiles table) â”€â”€
  const {
    register: regP,
    trigger: triggerP,
    getValues: getValuesP,
    reset: resetP,
    control: pfControl,
    formState: formStateP,
  } = useForm<UpdateStaffProfileInput>({
    resolver: zodResolver(updateStaffProfileSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        email: data.user.email,
        employee_code: data.user.employee_code ?? '',
        first_name: data.user.first_name ?? '',
        last_name: data.user.last_name ?? '',
        phone: data.user.phone,
        department_id: data.user.department_id,
        designation_id: data.user.designation_id,
        staff_type: data.user.staff_type,
        is_active: data.user.is_active,
        credit_start_date: data.user.credit_start_date?.slice(0, 10) ?? undefined,
      });
      resetP({
        dob: data.profile?.dob?.slice(0, 10) ?? undefined,
        gender: (['male', 'female', 'other'] as const).includes(data.profile?.gender as never)
          ? (data.profile?.gender as 'male' | 'female' | 'other')
          : undefined,
        marital_status: data.profile?.marital_status ?? undefined,
        blood_group: data.profile?.blood_group ?? undefined,
        emergency_contact: data.profile?.emergency_contact ?? undefined,
        father_name: data.profile?.father_name ?? undefined,
        mother_name: data.profile?.mother_name ?? undefined,
        spouse_name: data.profile?.spouse_name ?? undefined,
        physically_challenged: data.profile?.physically_challenged ?? false,
        address_line1: data.profile?.address_line1 ?? undefined,
        address_line2: data.profile?.address_line2 ?? undefined,
        city: data.profile?.city ?? undefined,
        state: data.profile?.state ?? undefined,
        postal_code: data.profile?.postal_code ?? undefined,
        joining_date: data.profile?.joining_date?.slice(0, 10) ?? undefined,
        work_start_time: data.profile?.work_start_time?.slice(0, 5) ?? '09:00',
        work_end_time: data.profile?.work_end_time?.slice(0, 5) ?? '19:00',
      });
    }
  }, [data, reset, resetP]);

  const { data: depts } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });
  const { data: desigs } = useQuery({
    queryKey: ['designations', 'all'],
    queryFn: () => designationsService.list({ limit: 100 }),
  });

  const update = useMutation({
    mutationFn: (input: UpdateStaffInput) =>
      usersService.update(id, {
        ...input,
        department_id: input.department_id || null,
        designation_id: input.designation_id || null,
      }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const updateProfile = useMutation({
    mutationFn: (input: UpdateStaffProfileInput) => usersService.updateStaffProfile(id, input),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const isSaving = update.isPending || updateProfile.isPending;

  // â”€â”€ Set password (independent action) â”€â”€
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const setPwd = useMutation({
    mutationFn: () => usersService.setPassword(id, newPwd),
    onSuccess: () => {
      toast.success('Password updated');
      setNewPwd('');
      setConfirmPwd('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pwdTooShort = newPwd.length > 0 && newPwd.length < 8;
  const pwdMismatch = confirmPwd.length > 0 && newPwd !== confirmPwd;
  const pwdValid = newPwd.length >= 8 && newPwd === confirmPwd;

  async function handleSaveAll() {
    const [basicOk, profileOk] = await Promise.all([trigger(), triggerP()]);
    if (!basicOk || !profileOk) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }
    await Promise.all([
      update.mutateAsync(getValues()),
      updateProfile.mutateAsync(getValuesP()),
    ]);
    toast.success('Staff updated');
    void queryClient.invalidateQueries({ queryKey: ['staff'] });
    navigate(`/hrms/staff/${id}`);
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="h-14 rounded-xl bg-muted animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border/60 bg-card p-4 space-y-3">
            <div className="h-4 w-32 rounded bg-muted animate-pulse" />
            <div className="grid gap-3 md:grid-cols-2">
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
              <div className="h-9 rounded-md bg-muted animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const { errors } = formState;
  const avatarUrl = data.new_photos_url ?? data.profile?.avatar_url;

  return (
    <div className="space-y-4">

      {/* â”€â”€ Page header â”€â”€ */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2"
            onClick={() => navigate(`/hrms/staff/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-primary/5">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserCircle2 className="h-6 w-6 text-primary/40" />
            )}
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight">
              {data.user.first_name} {data.user.last_name}
            </h1>
            <p className="text-xs text-muted-foreground">
              #{data.user.employee_code ?? '—'} · Edit Profile
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/hrms/staff/${id}`)}>
            Cancel
          </Button>
          <Button onClick={() => void handleSaveAll()} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* â”€â”€ Basic Info â”€â”€ */}
      <Card>
        <SectionHeader icon={<User className="h-3.5 w-3.5" />} title="Basic Info" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" placeholder="John" {...register('first_name')} />
              <FieldError message={errors.first_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" placeholder="Doe" {...register('last_name')} />
              <FieldError message={errors.last_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="john@example.com" {...register('email')} />
              <FieldError message={errors.email?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="employee_code">Employee Code</Label>
              <Input id="employee_code" placeholder="EMP001" {...register('employee_code')} />
              <FieldError message={errors.employee_code?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" placeholder="9876543210" {...register('phone')} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ? 'true' : 'false'}
                    onValueChange={(v) => field.onChange(v === 'true')}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Organization â”€â”€ */}
      <Card>
        <SectionHeader icon={<Briefcase className="h-3.5 w-3.5" />} title="Organization" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Controller
                name="department_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {(depts?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Designation</Label>
              <Controller
                name="designation_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {(desigs?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Staff Type</Label>
              <Controller
                name="staff_type"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? null : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {STAFF_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{STAFF_TYPE_LABELS[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credit_start_date" className="flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" /> Leave Credit Start Date
              </Label>
              <Controller
                control={control}
                name="credit_start_date"
                render={({ field }) => (
                  <DatePicker id="credit_start_date" value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
              <FieldError message={errors.credit_start_date?.message} />
              <p className="text-xs text-muted-foreground">Monthly leave credits begin from this date</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Personal Details â”€â”€ */}
      <Card>
        <SectionHeader icon={<User className="h-3.5 w-3.5" />} title="Personal Details" optional />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dob">Date of Birth</Label>
              <Controller
                control={pfControl}
                name="dob"
                render={({ field }) => (
                  <DatePicker id="dob" value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
              <FieldError message={formStateP.errors.dob?.message} />
            </div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Controller
                name="gender"
                control={pfControl}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? undefined : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select --</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Blood Group</Label>
              <Controller
                name="blood_group"
                control={pfControl}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? undefined : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Select blood group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select --</SelectItem>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bg) => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Marital Status</Label>
              <Controller
                name="marital_status"
                control={pfControl}
                render={({ field }) => (
                  <Select
                    value={field.value ?? '__none__'}
                    onValueChange={(v) => field.onChange(v === '__none__' ? undefined : v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select --</SelectItem>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="emergency_contact">Emergency Contact</Label>
              <Input id="emergency_contact" placeholder="Phone number" {...regP('emergency_contact')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="joining_date" className="flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" /> Joining Date
              </Label>
              <Controller
                control={pfControl}
                name="joining_date"
                render={({ field }) => (
                  <DatePicker id="joining_date" value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
              <FieldError message={formStateP.errors.joining_date?.message} />
            </div>
            <div className="flex items-center gap-2 pt-1 md:col-span-2">
              <Controller
                name="physically_challenged"
                control={pfControl}
                render={({ field }) => (
                  <Checkbox
                    id="physically_challenged"
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <Label htmlFor="physically_challenged" className="cursor-pointer font-normal">
                Physically Challenged
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Family Details â”€â”€ */}
      <Card>
        <SectionHeader icon={<Heart className="h-3.5 w-3.5" />} title="Family Details" optional />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="father_name">Father's Name</Label>
              <Input id="father_name" {...regP('father_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mother_name">Mother's Name</Label>
              <Input id="mother_name" {...regP('mother_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="spouse_name">Spouse Name</Label>
              <Input id="spouse_name" {...regP('spouse_name')} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Address â”€â”€ */}
      <Card>
        <SectionHeader icon={<MapPin className="h-3.5 w-3.5" />} title="Address" optional />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input id="address_line1" {...regP('address_line1')} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input id="address_line2" {...regP('address_line2')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" {...regP('city')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state">State</Label>
              <Input id="state" {...regP('state')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input id="postal_code" {...regP('postal_code')} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Working Hours â”€â”€ */}
      <Card>
        <SectionHeader icon={<Clock className="h-3.5 w-3.5" />} title="Working Hours" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Shift Start Time <span className="text-destructive">*</span></Label>
              <Controller
                control={pfControl}
                name="work_start_time"
                render={({ field }) => (
                  <TimePicker value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
              {formStateP.errors.work_start_time && (
                <p className="text-xs text-destructive">{formStateP.errors.work_start_time.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Shift End Time <span className="text-destructive">*</span></Label>
              <Controller
                control={pfControl}
                name="work_end_time"
                render={({ field }) => (
                  <TimePicker value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
              {formStateP.errors.work_end_time && (
                <p className="text-xs text-destructive">{formStateP.errors.work_end_time.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* â”€â”€ Security / Set Password â”€â”€ */}
      <Card>
        <SectionHeader icon={<KeyRound className="h-3.5 w-3.5" />} title="Set Password" optional />
        <CardContent className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Set a new login password for this employee. This signs them out of all devices.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
              {pwdTooShort && <FieldError message="Password must be at least 8 characters" />}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                placeholder="Re-enter password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
              />
              {pwdMismatch && <FieldError message="Passwords do not match" />}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!pwdValid || setPwd.isPending}
            onClick={() => setPwd.mutate()}
          >
            <KeyRound className="h-4 w-4" />
            {setPwd.isPending ? 'Updating...' : 'Set Password'}
          </Button>
        </CardContent>
      </Card>

    </div>
  );
}
